import React, { useEffect, useState } from "react";

export const DEVICE_STATUSES = [
  "MANUFACTURED",
  "TESTED",
  "PACKAGED",
  "SHIPPED",
  "DEPLOYED",
  "DAMAGED",
  "RETIRED",
];

const BASE_HARDWARE_REVISIONS = ["GND", "F8"];

export default function HardwareListFilterCard({
  onApplyFilter,
  currentFilters = {},
  searchValue = "",
  onSearchChange,
  hardwareRevisionOptions = [],
}) {
  const [preset, setPreset] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [hardwareRevision, setHardwareRevision] = useState("");
  const [status, setStatus] = useState("");
  const [isDamaged, setIsDamaged] = useState("");

  const yyyyMmDd = (d) =>
    new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 10);

  const computePresetRange = (value) => {
    const now = new Date();
    if (value === "all") {
      return { from: "", to: "" };
    }
    if (value === "this_year_to_date") {
      const start = new Date(now.getFullYear(), 0, 1);
      return { from: yyyyMmDd(start), to: yyyyMmDd(now) };
    }
    if (value === "last_year") {
      const start = new Date(now.getFullYear() - 1, 0, 1);
      const end = new Date(now.getFullYear() - 1, 11, 31);
      return { from: yyyyMmDd(start), to: yyyyMmDd(end) };
    }
    if (value === "last_month") {
      const firstDayThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endLastMonth = new Date(firstDayThisMonth - 1);
      const startLastMonth = new Date(
        endLastMonth.getFullYear(),
        endLastMonth.getMonth(),
        1,
      );
      return { from: yyyyMmDd(startLastMonth), to: yyyyMmDd(endLastMonth) };
    }
    return { from: dateFrom || "", to: dateTo || "" };
  };

  useEffect(() => {
    if (preset !== "custom") {
      const { from, to } = computePresetRange(preset);
      setDateFrom(from);
      setDateTo(to);
    }
  }, [preset]);

  useEffect(() => {
    if (currentFilters?.dateFrom) setDateFrom(currentFilters.dateFrom);
    if (currentFilters?.dateTo) setDateTo(currentFilters.dateTo);
    if (currentFilters?.hardwareRevision !== undefined)
      setHardwareRevision(currentFilters.hardwareRevision || "");
    if (currentFilters?.status !== undefined)
      setStatus(currentFilters.status || "");
    if (currentFilters?.isDamaged !== undefined)
      setIsDamaged(currentFilters.isDamaged || "");
  }, [currentFilters]);

  const revisionChoices = Array.from(
    new Set([...BASE_HARDWARE_REVISIONS, ...hardwareRevisionOptions].filter(Boolean)),
  ).sort();

  const handleApply = () => {
    const payload = {
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      hardwareRevision: hardwareRevision || undefined,
      status: status || undefined,
      isDamaged: isDamaged || undefined,
    };
    onApplyFilter(payload);
  };

  const handleClearFilters = () => {
    setPreset("");
    setDateFrom("");
    setDateTo("");
    setHardwareRevision("");
    setStatus("");
    setIsDamaged("");
    onSearchChange?.({ target: { value: "" } });
    onApplyFilter({});
  };

  const isCustom = preset === "custom";

  return (
    <div className="filter-card">
      <div className="filter-card-header">
        <div className="filter-card-title">Filters</div>
      </div>

      <div className="filter-row filter-row-top">
        <div className="filter-group">
          <div className="filter-label">Manufacture date</div>
          <div className="filter-date-controls">
            <select
              className="filter-input"
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
            >
              <option value="all">All</option>
              <option value="this_year_to_date">This Year-to-date</option>
              <option value="last_year">Last Year</option>
              <option value="last_month">Last Month</option>
              <option value="custom">Custom</option>
            </select>
            <input
              type="date"
              className="filter-input"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              disabled={!isCustom}
            />
            <input
              type="date"
              className="filter-input"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              disabled={!isCustom}
            />
          </div>
        </div>
        <div className="filter-group">
          <div className="filter-label">Search</div>
          <input
            type="text"
            className="filter-input"
            placeholder="Serial, batch, or BLE address…"
            value={searchValue}
            onChange={onSearchChange}
          />
        </div>
        <div className="filter-group">
          <div className="filter-label">Hardware rev</div>
          <select
            className="filter-input"
            value={hardwareRevision}
            onChange={(e) => setHardwareRevision(e.target.value)}
          >
            <option value="">All</option>
            {revisionChoices.map((rev) => (
              <option value={rev} key={rev}>
                {rev}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="filter-row">
        <div className="filter-group">
          <div className="filter-label">Status</div>
          <select
            className="filter-input"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All</option>
            {DEVICE_STATUSES.map((s) => (
              <option value={s} key={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <div className="filter-label">Is damaged</div>
          <select
            className="filter-input"
            value={isDamaged}
            onChange={(e) => setIsDamaged(e.target.value)}
          >
            <option value="">All</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        <div className="filter-actions">
          <button
            className="clear-filter-btn"
            type="button"
            onClick={handleClearFilters}
          >
            Clear filters
          </button>
          <button
            className="run-report-btn"
            type="button"
            onClick={handleApply}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}
